import type { FieldDestination } from './field-overview';
import type { FieldSnapshot } from '@mio/shared';

export const FIELD_REFERENCE_LIMIT = 20;

/** Resolve only submissions and reviews present in this learner's snapshot. */
export function fieldReviewReference(
  snapshot: FieldSnapshot,
  id: string,
): { label: string; destination: FieldDestination } | undefined {
  for (const pr of snapshot.prs) {
    if (pr.id === id)
      return {
        label: `Submission · ${pr.title} · ${pr.revision.slice(0, 8)}`,
        destination: { section: 'reviews', focusId: pr.id },
      };
    const review = pr.reviews.find((item) => `review-${item.id}` === id);
    if (review) {
      const actor = snapshot.actors.find((item) => item.id === review.actorId);
      return {
        label: `${actor?.name ?? 'Colleague'} review · ${pr.title} · ${review.revision.slice(0, 8)}${review.revision !== pr.revision ? ' (previous revision)' : ''}`,
        destination: { section: 'reviews', focusId: id },
      };
    }
  }
  return undefined;
}

export function addFieldReference(selected: string[], id: string) {
  return !id || selected.includes(id) || selected.length >= FIELD_REFERENCE_LIMIT
    ? selected
    : [...selected, id];
}

/** Keep references recognizable across direct messages, meetings and voice drafts. */
export function fieldReferenceOptions(snapshot: FieldSnapshot) {
  return [
    ...(snapshot.discovery
      ? [{ id: snapshot.discovery.brief.id, label: `Brief · ${snapshot.discovery.brief.title}` }]
      : []),
    ...(snapshot.discovery?.decisions.map((decision) => ({
      id: decision.id,
      label: `Decision · ${decision.title} · v${decision.version}`,
    })) ?? []),
    ...(snapshot.discovery?.findings.map((finding) => ({
      id: finding.id,
      label: `Finding · ${finding.title}`,
    })) ?? []),
    ...snapshot.tickets.map((ticket) => ({ id: ticket.id, label: `Ticket · ${ticket.title}` })),
    ...snapshot.runs.toReversed().map((run) => {
      const pr = snapshot.prs.find((candidate) => candidate.id === run.prId);
      return {
        id: run.id,
        label: `${run.suite.charAt(0).toUpperCase() + run.suite.slice(1)} run · ${pr?.title ?? 'Candidate'} · ${run.revision.slice(0, 8)}${pr && pr.revision !== run.revision ? ' (previous revision)' : ''} · ${run.status}${run.trusted ? ' · trusted' : ''} · #${run.id.slice(0, 8)}`,
      };
    }),
    ...snapshot.prs.map((pr) => ({
      id: pr.id,
      label: `Pull request · ${pr.title} · ${pr.revision.slice(0, 8)}`,
    })),
    ...snapshot.documents
      .filter((document) => document.status !== 'superseded')
      .map((document) => ({ id: document.id, label: `${document.title} · v${document.version}` })),
  ];
}
